import {
  bookingFingerprint,
  isValidIdempotencyKey,
} from "./idempotency";
import type {
  AgentRequest,
  BookAppointmentCall,
  GuardDecision,
} from "./types";
import type { CrmPort } from "../integrations/crm";

const CONFIRMATION_TOKEN = /^[A-Za-z0-9_-]{16,128}$/;

export async function evaluateBookingMutation(
  request: AgentRequest,
  call: BookAppointmentCall,
  crm: CrmPort,
): Promise<GuardDecision> {
  if (!isValidIdempotencyKey(call.input.idempotencyKey)) {
    return {
      allowed: false,
      reason: "invalid_idempotency_key",
    };
  }

  const confirmation = request.confirmation;
  if (
    !confirmation ||
    confirmation.action !== "book_appointment" ||
    confirmation.sessionId !== request.sessionId ||
    confirmation.sessionId !== call.input.sessionId ||
    confirmation.slot !== call.input.slot ||
    !CONFIRMATION_TOKEN.test(confirmation.token)
  ) {
    return {
      allowed: false,
      reason: "missing_explicit_confirmation",
    };
  }

  const existing = await crm.getIdempotencyRecord(call.input.idempotencyKey);
  if (existing) {
    const matches = existing.fingerprint === bookingFingerprint(call.input);
    return matches
      ? {
          allowed: true,
          replay: true,
        }
      : {
          allowed: false,
          reason: "idempotency_conflict",
        };
  }

  const fresh = await crm.getAvailability();
  if (fresh.version !== call.input.expectedVersion) {
    return {
      allowed: false,
      reason: "stale_snapshot",
    };
  }
  if (!fresh.slots.includes(call.input.slot)) {
    return {
      allowed: false,
      reason: "slot_not_available",
    };
  }

  return {
    allowed: true,
    replay: false,
  };
}
